import { getNominative } from '@lmc/common/utils'
import type { Home, ResidentDtos } from '@lmc/ts-common'
import type { RotaPatternResponse } from '@lmc/ts-common/lib/rota/dtos/rota-patterns'
import { EditTooltip } from '@lmc/ui-kit'
import { Button, Text } from '@lmc/ui-kit/src/general'
import { Table, type TableProps, Tag } from 'antd'
import type { SorterResult } from 'antd/es/table/interface'
import type { ColumnType } from 'antd/lib/table'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { startCase, upperFirst } from 'lodash'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import { VISIT } from '../constants'
import type { PageProps, Pagination } from '../hooks/useRotaPatternsList'
import type { RotaPatternModalType } from '../RotaPattern/utils'
import {
  DATE_FORMAT,
  getDateTimeInZone,
  getTimeInZone,
  TIME_FORMAT
} from '../utils.datetime'
import ServiceUser from './ServiceUser'

export type SortObject = {
  field: string
  order: 'ascend' | 'descend'
}

export type RotaTableProps = {
  tableType: RotaPatternModalType
  patternList: RotaPatternResponse[] | undefined
  isLoading?: boolean
  pagination: Pagination
  onChange: (props: PageProps) => void
  onSortChange: (sort: SortObject) => void
  hasResidentColumn?: boolean
}

type CustomElementsProps = {
  children: ReactNode
}

const RotaTable = ({
  tableType,
  patternList,
  isLoading,
  pagination = {
    currentPage: 1,
    itemsPerPage: 0,
    totalItems: 0
  },
  onChange,
  onSortChange,
  hasResidentColumn = true
}: RotaTableProps) => {
  const { t } = useTranslation()

  const pageSizes = [10, 50, 100]

  const preferredPeopleNominative = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative
  )
  const residentNominative = getNominative(
    preferredPeopleNominative,
    'Resident'
  )

  const flags = useFeatureFlags({
    flagNames: ['rostering_parked_features']
  })?.data

  const flagsRosteringParkedFeaturesEnabled =
    flags?.rostering_parked_features?.enabled

  const columns: ColumnType<RotaPatternResponse>[] = []

  if (tableType === VISIT && hasResidentColumn) {
    columns.push(
      {
        title: (
          <LeftmostCellContainer>
            <Text weight="medium">{startCase(residentNominative)}</Text>
          </LeftmostCellContainer>
        ),
        dataIndex: 'resident',
        key: 'resident.name.last',
        render: (resident: ResidentDtos) => (
          <LeftmostCellContainer>
            <CellContainer width={186}>
              <ServiceUser resident={resident} />
            </CellContainer>
          </LeftmostCellContainer>
        ),
        sorter: true
      },
      {
        title: <Text weight="medium">{t('TITLE')}</Text>,
        dataIndex: 'title',
        key: 'title',
        width: 250,
        render: (title: string) => (
          <CellContainer>
            <Text>{title}</Text>
          </CellContainer>
        )
      }
    )
  } else {
    columns.push({
      title: (
        <LeftmostCellContainer>
          <Text weight="medium">{t('TITLE')}</Text>
        </LeftmostCellContainer>
      ),
      dataIndex: 'title',
      key: 'title',
      render: (title: string) => (
        <LeftmostCellContainer>
          <CellContainer>
            <Text>{title}</Text>
          </CellContainer>
        </LeftmostCellContainer>
      )
    })
  }

  columns.push(
    {
      title: <Text weight="medium">{t('TYPE')}</Text>,
      dataIndex: 'instanceType',
      key: 'instanceType',
      render: (instanceType: string) => (
        <CellContainer width={120}>
          <Text>{startCase(instanceType)}</Text>
        </CellContainer>
      ),
      sorter: true
    },
    {
      title: <Text weight="medium">{startCase(t('TEAM'))}</Text>,
      dataIndex: 'numberSupply',
      key: 'numberSupply',
      render: (numberSupply: number) => (
        <CellContainer width={80}>
          <EditTooltip
            title={`${numberSupply} team member${
              Number(numberSupply) > 1 ? 's' : ''
            } required`}
          >
            <Text>{`${numberSupply}`}</Text>
          </EditTooltip>
        </CellContainer>
      ),
      sorter: true
    },
    {
      title: <Text weight="medium">{t('FREQUENCY')}</Text>,
      dataIndex: '',
      key: 'frequency',
      render: (value: { frequencyDescription: string }) => {
        return (
          <CellContainer width={120}>
            <Text>{upperFirst(value.frequencyDescription)}</Text>
          </CellContainer>
        )
      }
    },

    {
      title: <Text weight="medium">{startCase(t('TIME_WINDOW'))}</Text>,
      key: 'startTime',
      render: (text: string, record: RotaPatternResponse) => (
        <CellContainer width={120}>
          <TimeWindow>
            {tableType === VISIT &&
              record.flexiType &&
              flagsRosteringParkedFeaturesEnabled && (
                <TimeTags>
                  <TimeTagText level={2} weight="medium">
                    Flexi
                  </TimeTagText>
                  <TimeTagText level={2} weight="medium">
                    {record.expectedDuration}
                  </TimeTagText>
                </TimeTags>
              )}
            <StyledTag>
              <Text>{`${getTimeInZone(record.startTime).format(
                TIME_FORMAT
              )} - ${getTimeInZone(record.endTime).format(TIME_FORMAT)}`}</Text>
            </StyledTag>
          </TimeWindow>
        </CellContainer>
      ),
      sorter: true
    },
    {
      title: <Text weight="medium">{startCase(t('START_DATE'))}</Text>,
      dataIndex: '',
      key: 'patternStart',
      render: (value: RotaPatternResponse) => (
        <CellContainer width={92}>
          <Text>
            {getDateTimeInZone(value.patternStart).format(DATE_FORMAT)}
          </Text>
        </CellContainer>
      ),
      sorter: true
    },
    {
      title: ' ',
      dataIndex: 'id',
      key: 'view',
      render: (id: string) => (
        <CellContainer width={36} onClick={() => viewElement(id)}>
          <Button variant="text">
            <Text weight="medium">View</Text>
          </Button>
        </CellContainer>
      )
    }
  )

  const CustomCell = ({ children, ...props }: CustomElementsProps) => {
    return <CustomCellStyled {...props}>{children}</CustomCellStyled>
  }

  const CustomTable = ({ children, ...props }: CustomElementsProps) => {
    return <CustomTableStyled {...props}>{children}</CustomTableStyled>
  }

  const components = {
    table: CustomTable,
    body: {
      cell: CustomCell
    }
  }

  const viewElement = (id: string) => {
    window.open(`/rota/${id}/${tableType}`, '_blank')
  }

  const getRowClassName = (_record: RotaPatternResponse, index: number) => {
    return index % 2 === 0 ? 'even-row' : 'odd-row'
  }

  const handleTableChange: TableProps<RotaPatternResponse>['onChange'] = (
    pagination,
    filters,
    sorter
  ) => {
    const sortObject: SortObject = {
      field: '',
      order: 'ascend'
    }

    const applySortOrder = (s: SorterResult<RotaPatternResponse>) =>
      s.order
        ? { field: s.columnKey, order: s.order }
        : { field: s.columnKey, order: '' }

    if (Array.isArray(sorter)) {
      sorter.forEach((i) => {
        Object.assign(sortObject, applySortOrder(i))
      })
    } else {
      Object.assign(sortObject, applySortOrder(sorter))
    }

    onSortChange(sortObject)
  }

  return (
    <StyledTable<RotaPatternResponse>
      components={components}
      columns={columns}
      loading={isLoading}
      dataSource={patternList}
      rowClassName={getRowClassName}
      scroll={{ x: 'max-content' }}
      onChange={handleTableChange}
      pagination={{
        position: ['bottomCenter'],
        showSizeChanger: true,
        pageSize: pagination.itemsPerPage,
        hideOnSinglePage: false,
        pageSizeOptions: pageSizes,
        total: pagination.totalItems,
        current: pagination.currentPage,
        onChange: (page: number, size: number) => {
          onChange({ size, index: page })
        }
      }}
    />
  )
}

const StyledTable = styled(Table)`
  border-radius: 20px;
  overflow: hidden;

  & .even-row {
    background-color: ${({ theme }) => theme.color.neutral.color11};
  }

  & .odd-row {
    background-color: ${({ theme }) => theme.color.neutral.color10};
  }
` as unknown as typeof Table

const CustomTableStyled = styled.table`
  background: ${({ theme }) => theme.color.background.tertiary};
`

const CustomCellStyled = styled.td`
  padding: 16px 12px !important;
  background-color: inherit !important;
`

const CellContainer = styled.div<{ width?: number }>`
  width: ${(props) => props.width}px;
  color: ${({ theme }) => theme.color.neutral.color3};
`

const LeftmostCellContainer = styled.div`
  padding-left: 12px;
`

const TimeWindow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const TimeTags = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0 4px;
`

const TimeTagText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
  text-transform: uppercase;
`

const StyledTag = styled(Tag)`
  padding: 4px 8px;
  margin: 0;
  text-align: center;
  color: ${({ theme }) => theme.color.neutral.color3};
`
export default RotaTable
